import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { userAtom } from '../store/atoms';
import axiosService from '../services/axios.service';
import type { NotificationStatus, NotificationArea, NotificationDTO } from '../../../shared/types';

// Query key factory
export const notificationKeys = {
    all: ['notifications'] as const,
    crew: (slug: string) => [...notificationKeys.all, slug] as const,
    list: (slug: string, params?: object) => [...notificationKeys.crew(slug), 'list', params] as const,
    unreadCount: (slug: string) => [...notificationKeys.crew(slug), 'unread-count'] as const
};

interface NotificationListParams {
    status?: NotificationStatus;
    area?: NotificationArea;
    limit?: number;
    skip?: number;
}

interface NotificationListResponse {
    data: NotificationDTO[];
    total: number;
}

// API functions
const fetchNotifications = async (
    crewSlug: string,
    params: NotificationListParams = {}
): Promise<NotificationListResponse> => {
    const { data } = await axiosService.get(`/c/${crewSlug}/notifications`, { params });
    return { data: data.data, total: data.total };
};

const fetchUnreadCount = async (crewSlug: string): Promise<number> => {
    const { data } = await axiosService.get(`/c/${crewSlug}/notifications/unread-count`);
    return data.data.count;
};

// Hooks

/**
 * Fetches the notification list for the sidebar.
 * When sidebarOpen=true, refreshes every 15s to meet the ≤1 minute delivery requirement.
 */
export const useNotifications = (
    crewSlug: string,
    params: NotificationListParams = {},
    sidebarOpen = false
) => {
    const user = useAtomValue(userAtom);
    return useQuery<NotificationListResponse>({
        queryKey: notificationKeys.list(crewSlug, params),
        queryFn: () => fetchNotifications(crewSlug, params),
        enabled: !!crewSlug && !!user,
        refetchInterval: sidebarOpen ? 15_000 : false
    });
};

/**
 * Fetches the unread notification badge count.
 * SSE (useSse) is the primary delivery mechanism via query invalidation.
 * The 5-minute interval is a safety fallback only.
 */
export const useUnreadNotificationCount = (crewSlug: string) => {
    const user = useAtomValue(userAtom);
    return useQuery<number>({
        queryKey: notificationKeys.unreadCount(crewSlug),
        queryFn: () => fetchUnreadCount(crewSlug),
        enabled: !!crewSlug && !!user,
        refetchInterval: 5 * 60_000 // SSE is primary; this is just a safety net
    });
};

export const useMarkNotificationRead = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            axiosService.put(`/c/${crewSlug}/notifications/${id}/read`),
        onSuccess: () => {
            // Decrement the badge immediately — no need to wait for a refetch
            queryClient.setQueryData(
                notificationKeys.unreadCount(crewSlug),
                (old: number) => Math.max(0, (old ?? 1) - 1)
            );
            queryClient.invalidateQueries({ queryKey: notificationKeys.crew(crewSlug) });
        }
    });
};

export const useMarkAllNotificationsRead = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => axiosService.put(`/c/${crewSlug}/notifications/read-all`),
        onSuccess: () => {
            // Zero the badge immediately — we know the result without a refetch
            queryClient.setQueryData(notificationKeys.unreadCount(crewSlug), 0);
            queryClient.invalidateQueries({ queryKey: notificationKeys.crew(crewSlug) });
        }
    });
};

export const useArchiveNotification = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            axiosService.put(`/c/${crewSlug}/notifications/${id}/archive`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.crew(crewSlug) });
        }
    });
};
